/**
 * Countdowns.
 *
 * The estimate is the emotional centre of this product: a customer standing
 * outside wants to know how long, and a merchant wants to know which order is
 * about to breach its promise. Both read from the same component so the two
 * screens can never disagree about what "3 minutes left" means.
 *
 * Every readout takes its "now" from the shared clock, so twenty cards tick on
 * one schedule instead of twenty; see `providers/clock.tsx`. Formatting is
 * still `Intl`-backed through the helpers in `@yummy/shared`.
 */

import type { ReactNode } from 'react';
import { View } from 'react-native';

import {
  formatCountdown,
  formatCountdownCoarse,
  RUNNING_LATE_SECONDS,
  secondsSince,
  secondsUntil,
} from '@yummy/shared';

import { Text } from './ui/text';
import { Row } from './layout';
import { useNow } from '../providers/clock';
import { useT } from '../providers/locale';
import { cn } from '../lib/utils';
import { TONE_TEXT, type Tone } from '../lib/theme';

/**
 * The typography scale the previous kit exposed, retained so ported screens pass
 * the same `variant` values. Each step maps to one whole literal class string,
 * because Tailwind reads class names from source text.
 */
type TextVariant =
  | 'display'
  | 'title'
  | 'heading'
  | 'subheading'
  | 'body'
  | 'label'
  | 'caption'
  | 'micro';

/** Size and default weight per typography step, as whole literal class strings. */
const VARIANT_TEXT: Record<TextVariant, string> = {
  display: 'text-3xl font-bold',
  title: 'text-2xl font-bold',
  heading: 'text-xl font-semibold',
  subheading: 'text-base font-semibold',
  body: 'text-base font-normal',
  label: 'text-sm font-semibold',
  caption: 'text-xs font-normal',
  micro: 'text-[11px] font-normal',
};

/**
 * Props for {@link Countdown}: the instant it runs towards and how to render the
 * remaining time.
 */
export interface CountdownProps {
  /** ISO timestamp the countdown runs towards. */
  target: string;
  variant?: TextVariant;
  /** Forces a tone; by default the tone shifts as the deadline approaches. */
  tone?: Tone;
  /** Renders `12:34` rather than `about 12 min`. */
  precise?: boolean;
  prefix?: string;
  /** Copy shown once the target has passed; defaults to the localised "Ready". */
  overdueLabel?: string;
}

/**
 * Ticks down to an instant.
 *
 * Without an explicit `tone`, the colour escalates: normal until five minutes
 * out, amber inside five, red once overdue. That progression is the whole point
 * — a merchant scanning the queue sees risk without reading any numbers.
 *
 * @param props Target instant and presentation.
 * @returns A countdown label.
 */
/** How long before its target a countdown turns amber. */
const AMBER_LEAD_SECONDS = 300;

export function Countdown({
  target,
  variant = 'body',
  tone,
  precise,
  prefix,
  overdueLabel,
}: CountdownProps) {
  const t = useT();
  const now = useNow();

  const remaining = secondsUntil(target, now);
  const text =
    remaining <= 0
      ? (overdueLabel ?? t('time.ready'))
      : precise
        ? formatCountdown(remaining)
        : formatCountdownCoarse(remaining, t);

  const resolvedTone: Tone =
    // Amber inside the same five minutes the card later calls "running late" —
    // the warning before the deadline and the naming after it are one symmetric
    // window, and deliberately not the same constant: this one is a lead-in, that
    // one is a lateness, and a kitchen could reasonably want them apart.
    tone ?? (remaining <= 0 ? 'success' : remaining <= AMBER_LEAD_SECONDS ? 'warning' : 'neutral');

  // `TONE_TEXT` is the className counterpart of `toneColors(...).strong`, so the
  // tone still resolves through one record. Neutral reads as full foreground
  // rather than the muted tone colour, matching the original treatment.
  const toneClass = resolvedTone === 'neutral' ? 'text-foreground' : TONE_TEXT[resolvedTone];

  return (
    <Text numberOfLines={1} className={cn(VARIANT_TEXT[variant], 'font-bold', toneClass)}>
      {prefix ? `${prefix} ${text}` : text}
    </Text>
  );
}

/**
 * A countdown paired with a progress bar showing elapsed share of the estimate.
 *
 * Used on the customer's tracking screen, where the bar communicates progress
 * even when the number has stopped moving.
 *
 * @param props Target, the original estimate, and labels.
 * @returns The labelled countdown and its progress bar.
 */
export function CountdownProgress({
  target,
  totalSeconds,
  label,
  graceSeconds = RUNNING_LATE_SECONDS,
  settled = false,
  accepted = true,
  pendingGraceSeconds,
  pendingGraceFrom,
  lateAction,
}: {
  target: string;
  totalSeconds: number;
  label?: string;
  /**
   * The window before the card says nobody has accepted the order yet, and what
   * it is measured from.
   *
   * Separate from `graceSeconds` because they are different promises. The
   * accepted path's grace is measured from the estimate, which is right — by
   * then the kitchen has the order and is telling the customer how far behind
   * its own number it is. A pending order's window belongs to the merchant
   * instead, and may be measured from the order landing rather than from an
   * estimate that could be forty minutes long. Defaults to `graceSeconds` from
   * the target, which is what this did before the merchant could choose.
   */
  pendingGraceSeconds?: number;
  pendingGraceFrom?: string;
  /**
   * Whether the restaurant has taken the order on at all.
   *
   * `false` draws no bar and no countdown: there is no progress to show, and
   * once the estimate has passed the card says whose turn it is instead. See the
   * early return below.
   */
  accepted?: boolean;
  /**
   * What the customer can do about being kept waiting, set inside the sentence
   * as its last clause — so it arrives as one thought rather than two, and the
   * line reads as a whole rather than as a notice with a button under it.
   *
   * It should be a `Text`, because it is nested inside one. A sentence here
   * would have to know about phone numbers and how to dial them, which is the
   * screen's business rather than a progress bar's.
   */
  lateAction?: ReactNode;
  /**
   * How long past the estimate before the delay is named, in seconds.
   *
   * An estimate is a ballpark, so the moment it passes is not news. Until this
   * has elapsed the card says only that it is close — `0 min late`, which is
   * what an immediate label would read, is worse than saying nothing.
   */
  graceSeconds?: number;
  /**
   * Whether someone has marked the order ready.
   *
   * The bar fills with the clock, so it reaches the end when the estimate runs
   * out — whether or not the food is out. Green and `Ready for pickup` are
   * therefore reserved for this flag, which only a member of staff can set, and
   * it overrides the clock in both directions: an order ready ahead of its
   * estimate shows full and green rather than part-filled.
   */
  settled?: boolean;
}) {
  const t = useT();
  const now = useNow();

  const remaining = secondsUntil(target, now);
  const elapsed = Math.max(0, totalSeconds - remaining);
  const progress = totalSeconds > 0 ? Math.min(1, elapsed / totalSeconds) : 1;
  const overdue = remaining <= 0;
  const lateSeconds = Math.max(0, -remaining);
  // `RUNNING_LATE_SECONDS` by default, which is also where the board's Late band
  // begins: one number, so the counter can look at a late order and know the
  // customer has been told.
  const named = !settled && overdue && lateSeconds >= graceSeconds;

  // An order the kitchen has not taken on has nothing to draw. The estimate was
  // made at checkout, before anyone agreed to cook, so a bar and a countdown
  // would be measuring the customer's own wait against a guess — and the
  // estimate is remade on acceptance, so the clock belongs to the accepted
  // order. While there is still time on it the headline says the same thing, so
  // a sentence here would only repeat it.
  if (!accepted) {
    // A window rather than a lateness, so it is measured from its own anchor and
    // not from the estimate: an estimate is a ballpark, and "the restaurant has
    // not accepted this order" is as true at nought seconds as at three hours.
    // Which anchor is the merchant's to choose — see `pendingGraceFrom`.
    const pendingGrace = pendingGraceSeconds ?? graceSeconds;
    const since = secondsSince(pendingGraceFrom ?? target, now);
    return since >= pendingGrace ? (
      <Text className={cn('text-base font-medium', TONE_TEXT.warning)}>
        {t('customer.order.notAccepted')} {lateAction}
      </Text>
    ) : null;
  }

  return (
    <View className="gap-2">
      <Row spread gap="md">
        <Text className={cn(VARIANT_TEXT.label, 'text-muted-foreground')}>
          {settled ? t('customer.order.readyNow') : (label ?? t('customer.order.estimatedReady'))}
        </Text>
        {settled ? (
          <Text className={cn(VARIANT_TEXT.subheading, 'font-bold', TONE_TEXT.success)}>
            {t('customer.order.readyNow')}
          </Text>
        ) : named ? (
          // Tabular: the figure changes while it is being read.
          <Text
            className={cn(VARIANT_TEXT.subheading, 'font-bold tabular-nums', TONE_TEXT.warning)}>
            {/* Coarse units, not minutes: an order left unaccepted overnight
                reached `214 min late`, which is a number nobody converts. */}
            {t('customer.order.lateBy', {
              when: formatCountdownCoarse(Math.max(1, -remaining), t),
            })}
          </Text>
        ) : overdue ? (
          <Text className={cn(VARIANT_TEXT.subheading, 'font-bold', 'text-foreground')}>
            {t('customer.order.anyMomentNow')}
          </Text>
        ) : (
          <Countdown target={target} variant="subheading" />
        )}
      </Row>
      <View className="bg-muted h-2.5 overflow-hidden rounded-full">
        <View
          className={cn('h-full rounded-full', settled ? 'bg-success' : 'bg-primary')}
          // The fill's width is the value being measured, so it stays a style.
          style={{ width: `${(settled ? 1 : progress) * 100}%` }}
        />
      </View>
      {named ? (
        <Text className={cn('text-base font-medium', TONE_TEXT.warning)}>
          {t('customer.order.runningLate')} {lateAction}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * An "elapsed since placed" readout for the merchant queue.
 *
 * Turns red past a threshold, which is how a busy kitchen spots the order that
 * has been sitting too long — while it is still live. Given an `until` it freezes
 * at that instant and stays quiet, because a closed order's total is a record
 * rather than a warning.
 *
 * @param props The order's placement time, the instant it ended if it has, and a
 * warning threshold.
 * @returns The elapsed-time label.
 */
export function Elapsed({
  since,
  until,
  variant = 'caption',
  warnAfterSeconds = 900,
  format = 'ago',
}: {
  since: string;
  /**
   * The instant the thing being measured ended.
   *
   * Set on a closed order, and the readout stops there: the day's record wants
   * how long the order took, not how long ago it was taken — a clock that keeps
   * counting past the hand-over is measuring nothing. Undefined while the order
   * is live, which is when the number is still worth watching.
   */
  until?: string;
  variant?: TextVariant;
  warnAfterSeconds?: number;
  /** `bare` drops the "ago" and shows the duration alone. */
  format?: 'ago' | 'bare';
}) {
  const t = useT();
  const now = useNow();
  // A frozen readout counts to its end rather than from the clock, so the number
  // stops moving the moment the order does. `secondsSince` owns that arithmetic,
  // and `until` is tested against `undefined` rather than treated as falsy, so a
  // caller can only buy the frozen state by naming the instant.
  const elapsed = secondsSince(since, now, until);
  // Red is the live alarm — "this has been sitting too long" — and a closed order
  // cannot be late. The frozen total is a record, so it keeps the quiet colour.
  const overdue = until === undefined && elapsed > warnAfterSeconds;

  return (
    <Text
      className={cn(
        VARIANT_TEXT[variant],
        overdue ? 'font-bold text-destructive' : 'font-normal text-subtle-foreground'
      )}>
      {format === 'bare'
        ? formatCountdown(elapsed)
        : t('relative.ago', { time: formatCountdown(elapsed) })}
    </Text>
  );
}
